import React, { useState, useEffect, useCallback, useRef, useMemo, Suspense, lazy, memo } from "react";

const ProductClassificationSearch = lazy(() => import("./ProductClassificationSearch"));
const ProductClassificationSelect = lazy(() => import("./ProductClassificationSelect"));

// Countries cache - shared across component instances
const countriesCache = {
  export: null,
  import: null,
  origin: null,
};

const fetchCountries = async (scope) => {
  if (countriesCache[scope]) {
    return countriesCache[scope];
  }

  const response = await fetch(`/api/int/countries.json?scope=${scope}`, {
    credentials: 'same-origin'
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data = await response.json();
  countriesCache[scope] = data.groups;
  return data.groups;
};

const createLineItem = (defaults = {}, baseTree = {}) => ({
  itemDescription: defaults.itemDescription || "",
  itemCost: defaults.itemCost ?? 0.0,
  itemQuantity: defaults.itemQuantity || 1,
  selectedOriginCountryCode: defaults.selectedOriginCountryCode || defaults.selectedExportCountryCode || "",
  hasCertificateOfOrigin: defaults.hasCertificateOfOrigin ?? false,
  extraFields: defaults.extraFields || [],
  selectedLeafId: null,
  classificationLabel: null,
  classificationTree: JSON.parse(JSON.stringify(defaults.classificationTree ?? baseTree)),
  selectedValue: "",
  hideInput: false,
  collapsed: false,
});

const createInitialState = (props) => ({
  // Start with empty arrays, will be populated on mount
  importCountries: props.importCountries?.length ? props.importCountries : [],
  exportCountries: props.exportCountries?.length ? props.exportCountries : [],
  originCountries: props.originCountries?.length ? props.originCountries : [],
  countriesLoading: true,

  selectedExportCountryCode: props.selectedExportCountryCode,
  selectedImportCountryCode: props.selectedImportCountryCode,
  selectedImportStateCode: props.selectedImportStateCode,
  itemCostCurrencyCode: props.itemCostCurrencyCode,
  itemShippingCost: props.itemShippingCost ?? 0.0,
  itemInsuranceCost: props.itemInsuranceCost ?? 0.0,
  selectedModeOfTransport: props.selectedModeOfTransport,
  selectedImportType: props.selectedImportType,

  lineItems: [createLineItem(props)],
});

const FormSection = memo(function FormSection({ step, title, headerRight, children, className = "bg-section-primary" }) {
  return (
    <div className={`${className} delimiter-top delimiter-bottom`}>
      <div className="container">
        <h6 className="text-uppercase ls-2 font-weight-700">Step {step}</h6>
        <div className="d-flex align-items-center justify-content-between">
          <p className="lead lh-180 mb-0">{title}</p>
          {headerRight}
        </div>
        {children}
      </div>
    </div>
  );
});

const FormField = memo(function FormField({ label, required, children, className = "col-md-6 col-sm-12", hint }) {
  return (
    <div className={className}>
      <div className="form-group">
        <label className="form-control-label">
          {label}
          {required && <abbr title="required">*</abbr>}
        </label>
        {children}
        {hint && <small className="form-text text-muted mt-2">{hint}</small>}
      </div>
    </div>
  );
});

const NumericInput = memo(function NumericInput({ id, name, value, onChange, onWheel, required, placeholder = "0.0", min = "0.00", step = "0.01", inputMode = "decimal" }) {
  return (
    <input
      id={id}
      onChange={onChange}
      value={value}
      className={`form-control numeric ${required ? "required" : "optional"} form-control-lg`}
      required={required}
      aria-required={required}
      placeholder={placeholder}
      onWheel={onWheel}
      inputMode={inputMode}
      type="number"
      min={min}
      step={step}
      name={name}
    />
  );
});

const CurrencySelect = memo(function CurrencySelect({ name, value, onChange, currencies }) {
  return (
    <select
      name={name}
      value={value}
      onChange={onChange}
      className="form-control custom-select custom-select-lg"
      required
      aria-required
    >
      {currencies.map(([label, options]) => (
        <optgroup key={label} label={label}>
          {options.map((code) => (
            <option key={code} value={code}>{code}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
});

const renderOptions = (options) =>
  options.map((option) => {
    const optionValue = Array.isArray(option) ? option[0] : option.isoCode ?? option;
    const optionLabel = Array.isArray(option) ? option[1] : option.name ?? option;
    return (
      <option key={optionValue} value={optionValue}>
        {optionLabel}
      </option>
    );
  });

const isGroupedOptions = (options) =>
  options.length > 0 && typeof options[0] === "object" && "label" in options[0] && "countries" in options[0];

const SelectInput = memo(function SelectInput({ name, value, onChange, options, required, placeholder, loading }) {
  const grouped = isGroupedOptions(options);

  return (
    <select
      onChange={onChange}
      value={loading ? "" : value}
      className={`form-control custom-select custom-select-lg ${required ? "required" : ""}`}
      required={required}
      aria-required={required}
      name={name}
      disabled={loading}
    >
      {loading && (
        <option value="" disabled>
          Loading...
        </option>
      )}
      {placeholder && !loading && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {grouped
        ? options.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {renderOptions(group.countries)}
            </optgroup>
          ))
        : renderOptions(options)}
    </select>
  );
});

// Get the L4 classification label from a tree (falls back to deepest if fewer than 4 levels)
const getClassificationLabel = (tree) => {
  const labels = [];
  let node = tree;
  while (node?.selectedValue && node.selectOptions?.[node.selectedValue]) {
    const selected = node.selectOptions[node.selectedValue];
    if (selected.label) labels.push(selected.label);
    node = selected;
  }
  return labels[3] ?? labels[labels.length - 1] ?? null;
};

// Collapsed summary for a line item
const LineItemSummary = memo(function LineItemSummary({ item, index, onExpand, onRemove, canRemove }) {
  const description = item.itemDescription || `Item ${index + 1}`;
  const classification = item.classificationLabel || getClassificationLabel(item.classificationTree);

  return (
    <div className="card mb-3">
      <div
        className="card-body d-flex align-items-center justify-content-between py-3 px-4"
        style={{ cursor: "pointer" }}
        onClick={onExpand}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onExpand(); } }}
      >
        <div className="d-flex align-items-center flex-grow-1 mr-3" style={{ minWidth: 0 }}>
          <span className="text-truncate font-weight-600" style={{ maxWidth: "300px" }}>
            {description}
          </span>
          {classification && (
            <span className="text-muted ml-3 text-truncate" style={{ maxWidth: "300px" }}>
              {classification}
            </span>
          )}
        </div>
        <div className="d-flex align-items-center">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary mr-2"
            onClick={(e) => { e.stopPropagation(); onExpand(); }}
            title="Expand"
            aria-label="Expand item"
          >
            <i className="fas fa-chevron-down" aria-hidden="true" />
          </button>
          {canRemove && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={(e) => { e.stopPropagation(); onRemove(); }}
              title="Remove"
              aria-label="Remove item"
            >
              <i className="fas fa-trash-alt" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

// Single line item form (expanded)
const LineItemForm = memo(function LineItemForm({
  item,
  index,
  railsFormName,
  leafIndex,
  originCountries,
  countriesLoading,
  canRemove,
  canCollapse,
  onFieldChange,
  onOriginCountryChange,
  onClassificationChange,
  onLeafSelected,
  onCollapse,
  onRemove,
  handleWheelBlur,
}) {
  const prefix = `${railsFormName}[line_items][${index}]`;

  return (
    <div className="card mb-3">
      <div className="card-header d-flex align-items-center justify-content-between bg-white py-3">
        <div className="d-flex align-items-center">
          <strong>{item.itemDescription || `Item ${index + 1}`}</strong>
        </div>
        <div>
          {canCollapse && (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary mr-2"
              onClick={onCollapse}
              title="Collapse"
              aria-label="Collapse item"
            >
              <i className="fas fa-chevron-up" aria-hidden="true" />
            </button>
          )}
          {canRemove && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={onRemove}
              title="Remove"
              aria-label="Remove item"
            >
              <i className="fas fa-trash-alt" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
      <div className="card-body">
        <label className="form-control-label">
          <small className="form-text text-muted">
            By typing your product name below, we'll attempt to automatically
            classify your product. You can always change it or classify manually
            by using the 'Select category' dropdown below.
          </small>
        </label>
        <Suspense fallback={<div>Loading...</div>}>
          <ProductClassificationSearch
            value={item.itemDescription}
            handleClassificationChange={(suggestion) => onClassificationChange(index, suggestion)}
            railsFormName={prefix}
          />
          <ProductClassificationSelect
            classificationTree={item.classificationTree}
            railsFormName={prefix}
            leafIndex={leafIndex}
            handleLeafSelected={(value, label) => onLeafSelected(index, value, label)}
          />
        </Suspense>

        <div className="form-group row mt-4">
          <FormField label="Unit price" required className="col-md-4 col-sm-12">
            <NumericInput
              name={`${prefix}[unit_price]`}
              value={item.itemCost}
              onChange={(e) => onFieldChange(index, "itemCost", e.target.value)}
              onWheel={handleWheelBlur}
              required
              min="0.01"
            />
          </FormField>

          <FormField label="Item quantity" required className="col-md-4 col-sm-12">
            <NumericInput
              name={`${prefix}[quantity]`}
              value={item.itemQuantity}
              onChange={(e) => onFieldChange(index, "itemQuantity", e.target.value)}
              onWheel={handleWheelBlur}
              required
              placeholder="1"
              min="1"
              step="1"
              inputMode="numeric"
            />
          </FormField>

          <FormField
            label="Country of origin"
            required
            className="col-md-4 col-sm-12"
          >
            <SelectInput
              name={`${prefix}[origin_country_code]`}
              value={item.selectedOriginCountryCode}
              onChange={(e) => onOriginCountryChange(index, e.target.value)}
              options={originCountries}
              required
              placeholder="Select country of origin"
              loading={countriesLoading}
            />
          </FormField>

          <div className="col-12">
            <div className="form-group mt-2">
              <div className="custom-control custom-checkbox">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id={`has-certificate-of-origin-${index}`}
                  name={`${prefix}[certificate_of_origin]`}
                  value="1"
                  checked={item.hasCertificateOfOrigin}
                  onChange={(e) => onFieldChange(index, "hasCertificateOfOrigin", e.target.checked)}
                  aria-describedby={`certificate-of-origin-hint-${index}`}
                />
                <label className="custom-control-label" htmlFor={`has-certificate-of-origin-${index}`}>
                  I have a Certificate of Origin
                </label>
              </div>
              <small id={`certificate-of-origin-hint-${index}`} className="form-text text-muted mt-2">
                A Certificate of Origin is a document that certifies the country where the goods were produced. It may be
                required to claim preferential tariff rates under a trade agreement.
              </small>
            </div>
          </div>
        </div>

        {item.extraFields?.length > 0 && (
          <div className="form-group row">
            {item.extraFields.map((field) => (
              <FormField key={field.unit_name} label={`Measurements (in ${field.unit_name})`} required>
                <input
                  name={`${prefix}${field.unit_field_name}`}
                  value={field.unit_name}
                  type="hidden"
                />
                <NumericInput
                  id={`measurements_${index}_${field.unit_name}`}
                  name={`${prefix}${field.value_field_name}`}
                  value={item[`measurements_${field.unit_name}`] || ""}
                  onChange={(e) => onFieldChange(index, `measurements_${field.unit_name}`, e.target.value)}
                  onWheel={handleWheelBlur}
                  required
                />
              </FormField>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

// Cache for memoized handlers to avoid recreating on each render
const useInputHandlers = (handleInputChange) => {
  const handlersCache = useRef({});
  
  return useCallback((attrName) => {
    if (!handlersCache.current[attrName]) {
      handlersCache.current[attrName] = (event) => handleInputChange(event, attrName);
    }
    return handlersCache.current[attrName];
  }, [handleInputChange]);
};

function NewLandedCostCalculatorForm({
  classificationTree = {},
  leafIndex = 0,
  railsFormName = "",
  modesOfTransport,
  importTypes,
  currencies,
  ...restProps
}) {
  const extraFieldsCache = useRef({});
  const activeFetchControllers = useRef({});
  const baseClassificationTree = useRef(classificationTree);

  const [state, setState] = useState(() =>
    createInitialState({ classificationTree, ...restProps })
  );

  // Lazy load countries on mount
  useEffect(() => {
    if (state.exportCountries.length > 0) {
      setState((prev) => ({ ...prev, countriesLoading: false }));
      return;
    }

    let cancelled = false;

    const loadAllCountries = async () => {
      try {
        const [exportCountries, importCountries, originCountries] = await Promise.all([
          fetchCountries('export'),
          fetchCountries('import'),
          fetchCountries('origin'),
        ]);

        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            exportCountries,
            importCountries,
            originCountries,
            countriesLoading: false,
          }));
        }
      } catch (error) {
        console.error("Error loading countries:", error);
        if (!cancelled) {
          setState((prev) => ({ ...prev, countriesLoading: false }));
        }
      }
    };

    loadAllCountries();

    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchExtraFields = useCallback((selectedLeafId, countryCode, itemIndex, originCode = "", modeOfTransport = "", exportCode = "") => {
    if (!selectedLeafId || !countryCode) return;

    const cacheKey = `${selectedLeafId}-${countryCode}-${originCode}-${modeOfTransport}-${exportCode}`;
    if (extraFieldsCache.current[cacheKey]) {
      setState((prev) => {
        const newItems = [...prev.lineItems];
        if (newItems[itemIndex]) {
          newItems[itemIndex] = { ...newItems[itemIndex], extraFields: extraFieldsCache.current[cacheKey] };
        }
        return { ...prev, lineItems: newItems };
      });
      return;
    }

    activeFetchControllers.current[itemIndex]?.abort();
    activeFetchControllers.current[itemIndex] = new AbortController();

    const params = new URLSearchParams({
      product_classification_id: selectedLeafId,
      import_country_iso: countryCode,
    });
    if (originCode) params.set("origin_country_iso", originCode);
    if (modeOfTransport) params.set("mode_of_transport", modeOfTransport);
    if (exportCode) params.set("export_country_iso", exportCode);

    fetch(
      `/api/int/landed_cost_calculator/extra_fields.json?${params.toString()}`,
      { method: "GET", signal: activeFetchControllers.current[itemIndex].signal }
    )
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.json();
      })
      .then((data) => {
        extraFieldsCache.current[cacheKey] = data.extra_fields;
        setState((prev) => {
          const newItems = [...prev.lineItems];
          if (newItems[itemIndex]) {
            newItems[itemIndex] = { ...newItems[itemIndex], extraFields: data.extra_fields };
          }
          return { ...prev, lineItems: newItems };
        });
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          console.error("Error fetching extra fields:", error);
        }
      });
  }, []);

  useEffect(() => {
    return () => {
      Object.values(activeFetchControllers.current).forEach((c) => c?.abort());
    };
  }, []);

  // Auto-expand collapsed items when HTML5 validation fails on a hidden field.
  // The `invalid` event does not bubble, so we use capture phase.
  const containerRef = useRef(null);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleInvalid = (e) => {
      const wrapper = e.target.closest('[data-line-item-index]');
      if (!wrapper) return;
      const idx = parseInt(wrapper.dataset.lineItemIndex, 10);

      setState((prev) => {
        if (!prev.lineItems[idx]?.collapsed) return prev;
        const newItems = [...prev.lineItems];
        newItems[idx] = { ...newItems[idx], collapsed: false };
        return { ...prev, lineItems: newItems };
      });
    };

    container.addEventListener('invalid', handleInvalid, true);
    return () => container.removeEventListener('invalid', handleInvalid, true);
  }, []);

  // Order-level input handlers
  const handleInputChange = useCallback((event, attrName) => {
    const { target } = event;
    const value = target.type === "checkbox" ? target.checked : target.value;
    setState((prev) => (prev[attrName] === value ? prev : { ...prev, [attrName]: value }));
  }, []);

  const getInputHandler = useInputHandlers(handleInputChange);

  const handleWheelBlur = useCallback((event) => {
    event.currentTarget.blur();
  }, []);

  // Line item field change
  const handleLineItemFieldChange = useCallback((itemIndex, fieldName, value) => {
    setState((prev) => {
      const newItems = [...prev.lineItems];
      if (newItems[itemIndex] && newItems[itemIndex][fieldName] !== value) {
        newItems[itemIndex] = { ...newItems[itemIndex], [fieldName]: value };
        return { ...prev, lineItems: newItems };
      }
      return prev;
    });
  }, []);

  const updateClassificationTree = useCallback((tree, ancestryIds) => {
    const newTree = JSON.parse(JSON.stringify(tree));
    let currentNode = newTree;

    ancestryIds.forEach((id) => {
      currentNode.selectOptions = currentNode.selectOptions || {};
      currentNode.selectOptions[id] = currentNode.selectOptions[id] || { selectOptions: {} };
      currentNode.selectedValue = id;
      currentNode = currentNode.selectOptions[id];
    });

    return newTree;
  }, []);

  // Line item leaf selected
  const handleLeafSelected = useCallback(
    (itemIndex, selectedValue, label) => {
      setState((prev) => {
        const newItems = [...prev.lineItems];
        newItems[itemIndex] = {
          ...newItems[itemIndex],
          selectedLeafId: selectedValue,
          classificationLabel: label || null,
        };
        fetchExtraFields(selectedValue, prev.selectedImportCountryCode, itemIndex, newItems[itemIndex].selectedOriginCountryCode, prev.selectedModeOfTransport, prev.selectedExportCountryCode);
        return { ...prev, lineItems: newItems };
      });
    },
    [fetchExtraFields]
  );

  // Line item classification change (from auto-suggest)
  const handleClassificationChange = useCallback(
    (itemIndex, selectedAutoSuggestion) => {
      if (selectedAutoSuggestion.ancestry_ids.length > 0) {
        const lastAncestryId = selectedAutoSuggestion.ancestry_ids.at(-1);

        setState((prev) => {
          const newItems = [...prev.lineItems];
          const updatedTree = updateClassificationTree(
            newItems[itemIndex].classificationTree,
            selectedAutoSuggestion.ancestry_ids
          );
          newItems[itemIndex] = {
            ...newItems[itemIndex],
            selectedLeafId: lastAncestryId,
            hideInput: true,
            classificationTree: updatedTree,
            classificationLabel: selectedAutoSuggestion.name || null,
          };
          fetchExtraFields(lastAncestryId, prev.selectedImportCountryCode, itemIndex, newItems[itemIndex].selectedOriginCountryCode, prev.selectedModeOfTransport, prev.selectedExportCountryCode);
          return { ...prev, lineItems: newItems };
        });
      } else {
        setState((prev) => {
          const newItems = [...prev.lineItems];
          newItems[itemIndex] = { ...newItems[itemIndex], hideInput: true };
          return { ...prev, lineItems: newItems };
        });
      }
    },
    [updateClassificationTree, fetchExtraFields]
  );

  // Import country change - re-fetch extra fields for all items
  const handleCountryChange = useCallback(
    (event) => {
      const { value } = event.target;
      setState((prev) => {
        prev.lineItems.forEach((item, idx) => {
          fetchExtraFields(item.selectedLeafId, value, idx, item.selectedOriginCountryCode, prev.selectedModeOfTransport, prev.selectedExportCountryCode);
        });
        return {
          ...prev,
          selectedImportCountryCode: value,
          selectedImportStateCode: "",
        };
      });
    },
    [fetchExtraFields]
  );

  // Export country change - re-fetch extra fields for all items (customs union
  // membership depends on the export country)
  const handleExportCountryChange = useCallback(
    (event) => {
      const { value } = event.target;
      setState((prev) => {
        prev.lineItems.forEach((item, idx) => {
          fetchExtraFields(item.selectedLeafId, prev.selectedImportCountryCode, idx, item.selectedOriginCountryCode, prev.selectedModeOfTransport, value);
        });
        return { ...prev, selectedExportCountryCode: value };
      });
    },
    [fetchExtraFields]
  );

  // Mode of transport change - re-fetch extra fields for all items (charges can
  // be restricted by mode of transport)
  const handleModeOfTransportChange = useCallback(
    (event) => {
      const { value } = event.target;
      setState((prev) => {
        prev.lineItems.forEach((item, idx) => {
          fetchExtraFields(item.selectedLeafId, prev.selectedImportCountryCode, idx, item.selectedOriginCountryCode, value, prev.selectedExportCountryCode);
        });
        return { ...prev, selectedModeOfTransport: value };
      });
    },
    [fetchExtraFields]
  );

  // Origin country change for a line item - re-fetch its extra fields (charges
  // can be restricted to / excluded from specific origin countries)
  const handleOriginCountryChange = useCallback(
    (itemIndex, value) => {
      setState((prev) => {
        const newItems = [...prev.lineItems];
        if (newItems[itemIndex]) {
          newItems[itemIndex] = { ...newItems[itemIndex], selectedOriginCountryCode: value };
        }
        fetchExtraFields(newItems[itemIndex]?.selectedLeafId, prev.selectedImportCountryCode, itemIndex, value, prev.selectedModeOfTransport, prev.selectedExportCountryCode);
        return { ...prev, lineItems: newItems };
      });
    },
    [fetchExtraFields]
  );

  // Add a new line item (max 5)
  const MAX_LINE_ITEMS = 5;
  const handleAddLineItem = useCallback(() => {
    setState((prev) => {
      if (prev.lineItems.length >= MAX_LINE_ITEMS) return prev;
      const collapsedItems = prev.lineItems.map((item) => ({ ...item, collapsed: true }));
      const newItem = createLineItem(
        { selectedExportCountryCode: prev.selectedExportCountryCode },
        baseClassificationTree.current
      );
      return { ...prev, lineItems: [...collapsedItems, newItem] };
    });
  }, []);

  // Remove a line item
  const handleRemoveLineItem = useCallback((itemIndex) => {
    setState((prev) => {
      if (prev.lineItems.length <= 1) return prev;
      const newItems = prev.lineItems.filter((_, i) => i !== itemIndex);
      return { ...prev, lineItems: newItems };
    });
  }, []);

  // Toggle collapse
  const handleToggleCollapse = useCallback((itemIndex) => {
    setState((prev) => {
      const newItems = [...prev.lineItems];
      newItems[itemIndex] = { ...newItems[itemIndex], collapsed: !newItems[itemIndex].collapsed };
      return { ...prev, lineItems: newItems };
    });
  }, []);

  // Expand a collapsed item
  const handleExpandItem = useCallback((itemIndex) => {
    setState((prev) => {
      const newItems = [...prev.lineItems];
      newItems[itemIndex] = { ...newItems[itemIndex], collapsed: false };
      return { ...prev, lineItems: newItems };
    });
  }, []);

  // Memoize country lookup
  const selectedImportCountry = useMemo(
    () => state.importCountries.flatMap((g) => g.countries).find((c) => c.isoCode === state.selectedImportCountryCode),
    [state.importCountries, state.selectedImportCountryCode]
  );

  const subdivisionOptions = useMemo(
    () => selectedImportCountry?.subdivisions?.map(([name, code]) => ({ name, isoCode: code })) ?? [],
    [selectedImportCountry?.subdivisions]
  );

  return (
    <div ref={containerRef}>
      <FormSection step={1} title="Where are you shipping?">
        <div className="form-group row mt-3">
          <FormField label="From" required>
            <SelectInput
              name="duty_calculator_form[export_country_code]"
              value={state.selectedExportCountryCode}
              onChange={handleExportCountryChange}
              options={state.exportCountries}
              required
              loading={state.countriesLoading}
            />
          </FormField>

          <FormField label="To" required>
            <SelectInput
              name="duty_calculator_form[import_country_code]"
              value={state.selectedImportCountryCode}
              onChange={handleCountryChange}
              options={state.importCountries}
              required
              loading={state.countriesLoading}
            />
          </FormField>

          {selectedImportCountry?.requiresStateSelect && (
            <FormField label="Region" required className="col-12">
              <SelectInput
                name="duty_calculator_form[import_state_code]"
                value={state.selectedImportStateCode}
                onChange={getInputHandler("selectedImportStateCode")}
                options={subdivisionOptions}
                required
                placeholder="Select region"
              />
            </FormField>
          )}
        </div>
      </FormSection>

      <FormSection
        step={2}
        title="Tell us what you're shipping"
        className="bg-section-secondary py-4"
        headerRight={
          <div className="d-none d-md-flex align-items-center">
            <label className="form-control-label mb-0 mr-2 text-nowrap" style={{ fontSize: "0.875rem" }}>
              Currency
            </label>
            <CurrencySelect
              name="duty_calculator_form[currency_code]"
              value={state.itemCostCurrencyCode}
              onChange={getInputHandler("itemCostCurrencyCode")}
              currencies={currencies}
            />
          </div>
        }
      >
        <div className="d-md-none mt-3">
          <FormField label="Currency" required className="w-100">
            <CurrencySelect
              name="duty_calculator_form[currency_code]"
              value={state.itemCostCurrencyCode}
              onChange={getInputHandler("itemCostCurrencyCode")}
              currencies={currencies}
            />
          </FormField>
        </div>
        <div className="mt-4">
          {state.lineItems.map((item, index) =>
            <div key={index} data-line-item-index={index}>
              {item.collapsed && (
                <LineItemSummary
                  item={item}
                  index={index}
                  onExpand={() => handleExpandItem(index)}
                  onRemove={() => handleRemoveLineItem(index)}
                  canRemove={state.lineItems.length > 1}
                />
              )}
              <div style={{ display: item.collapsed ? 'none' : undefined }}>
                <LineItemForm
                  item={item}
                  index={index}
                  railsFormName={railsFormName}
                  leafIndex={leafIndex}
                  originCountries={state.originCountries}
                  countriesLoading={state.countriesLoading}
                  canRemove={state.lineItems.length > 1}
                  canCollapse
                  onFieldChange={handleLineItemFieldChange}
                  onOriginCountryChange={handleOriginCountryChange}
                  onClassificationChange={handleClassificationChange}
                  onLeafSelected={handleLeafSelected}
                  onCollapse={() => handleToggleCollapse(index)}
                  onRemove={() => handleRemoveLineItem(index)}
                  handleWheelBlur={handleWheelBlur}
                />
              </div>
            </div>
          )}
        </div>

        {state.lineItems.length < MAX_LINE_ITEMS && (
          <button
            type="button"
            className="btn btn-outline-primary btn-block mt-3"
            onClick={handleAddLineItem}
          >
            + Add another item
          </button>
        )}
      </FormSection>

      <FormSection step={3} title="How are you shipping?" className="bg-section-primary pt-4">
        <div className="form-group row">
          <FormField label="Shipping cost" required>
            <NumericInput
              name="duty_calculator_form[shipping_cost]"
              value={state.itemShippingCost}
              onChange={getInputHandler("itemShippingCost")}
              onWheel={handleWheelBlur}
              required
            />
          </FormField>

          <FormField label="Insurance cost" required>
            <NumericInput
              name="duty_calculator_form[insurance_cost]"
              value={state.itemInsuranceCost}
              onChange={getInputHandler("itemInsuranceCost")}
              onWheel={handleWheelBlur}
              required
            />
          </FormField>

          {selectedImportCountry?.requiresModeOfTransport && (
            <FormField label="Mode of transport">
              <SelectInput
                name="duty_calculator_form[mode_of_transport]"
                value={state.selectedModeOfTransport}
                onChange={handleModeOfTransportChange}
                options={modesOfTransport}
              />
            </FormField>
          )}

          {selectedImportCountry?.requiresImportType && (
            <FormField label="Import type">
              <SelectInput
                name="duty_calculator_form[import_type]"
                value={state.selectedImportType}
                onChange={getInputHandler("selectedImportType")}
                options={importTypes}
              />
            </FormField>
          )}
        </div>
      </FormSection>
    </div>
  );
}

export default memo(NewLandedCostCalculatorForm);
